import { z } from 'zod';
/**
 * Catálogo de tipos de campo de respuesta · cerrado, como el de eventos.
 *
 * **Mandato vigente: un campo de respuesta no es un `string` ni un `number`
 * pelado.** Un puntaje declara su rango; una respuesta categórica declara su
 * lista de opciones; una binaria declara cuánto suma cada lado. La razón es la
 * misma por la que el catálogo de eventos es cerrado: lo que no está declarado
 * no se puede validar, no se puede puntuar sin adivinar y no se puede proyectar
 * a FHIR sin inventar. Un `dolor: number` compila igual con 8 que con 800.
 *
 * Hasta E3.25 eran **tres tipos**, uno por cada forma de los cuatro
 * instrumentos y del microcheck:
 *
 *   - `puntaje`: número entero dentro de un rango explícito (dolor 0–10, VAS
 *     0–100, nivel EQ-5D 1–5).
 *   - `opcion`: una de una lista cerrada, cada una con su código estable y,
 *     cuando el instrumento puntúa, sus puntos (NMS, CAS-E, microcheck).
 *   - `binario`: sí/no con puntos por lado (los quince ítems del Katz-15).
 *
 * El archivo decía que un cuarto tipo era una decisión y no un reflejo. **Ésta
 * es la decisión (E3.25, D-255):** los cuestionarios de hito del protocolo de
 * Fundación (D-01, `dev/auditoria-datos.md`) piden datos que ninguno de los tres
 * puede decir sin mentir, y se suman cuatro, cada uno por una forma real:
 *
 *   - `numero`: una cantidad con unidad, entera o con decimales, con mínimo y
 *     máximo obligatorios: la potencia de un medicamento en miligramos, el
 *     intervalo entre tomas en horas. No es `puntaje` porque no es un ordinal
 *     de un instrumento: es una magnitud, lleva unidad UCUM, admite decimales
 *     (2,5 mg) y nunca puntúa.
 *   - `fecha`: un día del calendario, ISO, con rango relativo (no futura; no
 *     anterior al día 0 del programa cuando la pregunta lo pide): la fecha de
 *     una reinternación. No es `opcion` porque la lista de días no se enumera.
 *   - `catalogo`: una entrada de un catálogo de datos versionado, por su código
 *     estable: la droga, de la lista de principios activos de Fundación. **No es
 *     `opcion`** porque la lista no vive en la definición del formulario: la
 *     arma Fundación, crece sin tocar el código y una entrada se retira sin
 *     borrarse. Si viviera en la definición, sumar una droga sería cambiar la
 *     versión del `Questionnaire`.
 *   - `grupo`: un conjunto de preguntas que se repite de cero a N veces, con
 *     mínimo y máximo: la lista de medicamentos es un grupo de tres preguntas
 *     (droga, potencia, intervalo) por fila. No se puede aplanar: tres
 *     medicamentos no son nueve preguntas sueltas, y sin la fila no se sabe qué
 *     potencia es de qué droga.
 *
 * Un octavo tipo sigue siendo una decisión. Lo primero a revisar es si el dato
 * nuevo está bien modelado con estos siete.
 *
 * **Ningún tipo admite texto libre.** No es una omisión: la interpretación de
 * lenguaje libre está diferida (`CLAUDE.md` §4.1) y un campo de texto en un
 * formulario clínico es la vía más corta a narrativa del paciente dentro de la
 * base operativa. El catálogo tampoco lo abre: la droga que no está en la lista
 * se responde con la salida "no está en la lista", que es un código y no un
 * nombre escrito a mano. La búsqueda del buscador filtra la lista; no escribe.
 */
// ─────────────────────────────────────────────────────────────────────────────
// El catálogo
// ─────────────────────────────────────────────────────────────────────────────
export const TIPOS_DE_CAMPO = [
    'puntaje',
    'opcion',
    'binario',
    'numero',
    'fecha',
    'catalogo',
    'grupo',
];
export const tipoDeCampoSchema = z.enum(TIPOS_DE_CAMPO);
/**
 * Código de una opción. Kebab-case y estable: viaja al `QuestionnaireResponse`
 * y a los reportes, así que renombrarlo rompe la serie longitudinal.
 */
const codigoDeOpcion = z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'el código de una opción es kebab-case');
export const opcionDeRespuestaSchema = z.strictObject({
    codigo: codigoDeOpcion,
    /** Lo que lee el paciente. Redacción oficial cuando el instrumento la tiene. */
    etiqueta: z.string().min(1),
    /**
     * Puntos que aporta al instrumento. Ausente en las preguntas que se registran
     * pero no puntúan (P1, P3 y P4 del microcheck).
     */
    puntos: z.number().int().optional(),
});
export const campoPuntajeSchema = z.strictObject({
    tipo: z.literal('puntaje'),
    minimo: z.number().int(),
    maximo: z.number().int(),
    /** Anclas de los extremos ("Nada" / "Mucho" del P-05). */
    etiquetaMinimo: z.string().min(1).optional(),
    etiquetaMaximo: z.string().min(1).optional(),
    /**
     * Si el valor suma al puntaje del instrumento. El dolor no puntúa: reporta.
     *
     * Sin default a propósito: un campo de puntaje tiene que decir explícitamente
     * si entra al score. El default silencioso es cómo un dato de reporte termina
     * sumando a una escala sin que nadie lo haya decidido.
     */
    puntua: z.boolean(),
});
export const campoOpcionSchema = z.strictObject({
    tipo: z.literal('opcion'),
    opciones: z.array(opcionDeRespuestaSchema).min(2),
});
export const campoBinarioSchema = z.strictObject({
    tipo: z.literal('binario'),
    etiquetaSi: z.string().min(1),
    etiquetaNo: z.string().min(1),
    puntosSi: z.number().int(),
    puntosNo: z.number().int(),
});
/**
 * Unidad de una cantidad · UCUM, que es lo que pide FHIR.
 *
 * `ucum` es el código que viaja (`mg`, `h`); `etiqueta`, lo que lee el paciente
 * al lado del número ("mg", "horas"). Van separados porque no siempre coinciden
 * y porque el código no se traduce.
 */
export const unidadSchema = z.strictObject({
    ucum: z.string().min(1).max(20),
    etiqueta: z.string().min(1).max(20),
});
export const campoNumeroSchema = z.strictObject({
    tipo: z.literal('numero'),
    unidad: unidadSchema,
    /**
     * Cuántos decimales admite. `0` es un entero (horas entre tomas) y se proyecta
     * como `integer`; más de cero es un `decimal` (2,5 mg). Tope 3: más precisión
     * que un miligramo con tres decimales no la tiene ninguna presentación.
     */
    decimales: z.number().int().min(0).max(3),
    /** Obligatorios, sin default: una cantidad sin rango valida cualquier cosa. */
    minimo: z.number(),
    maximo: z.number(),
});
export const campoFechaSchema = z.strictObject({
    tipo: z.literal('fecha'),
    /**
     * Las dos restricciones del rango, explícitas y sin default, como `puntua`.
     * `noFutura`: lo que se pregunta ya pasó (una reinternación de la semana que
     * viene no se registra). `desdeElDiaCero`: no puede ser anterior al egreso,
     * porque lo que se pregunta es lo que pasó durante el programa.
     */
    noFutura: z.boolean(),
    desdeElDiaCero: z.boolean(),
});
/**
 * Los catálogos de datos que un campo puede nombrar. Cerrado: un catálogo nuevo
 * es una tabla nueva y un comando de carga nuevo, no un string en una definición.
 */
export const CATALOGOS_DE_DATOS = ['medicamentos'];
export const catalogoDeDatosSchema = z.enum(CATALOGOS_DE_DATOS);
/**
 * Código estable de una entrada de catálogo. Más amplio que el de una opción
 * porque no lo elegimos nosotros: los códigos de la lista de Fundación pueden
 * venir de alfabeta, con mayúsculas y puntos.
 */
export const codigoDeCatalogoSchema = z
    .string()
    .min(1)
    .max(40)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'el código de una entrada de catálogo no lleva espacios');
export const campoCatalogoSchema = z.strictObject({
    tipo: z.literal('catalogo'),
    catalogo: catalogoDeDatosSchema,
    /**
     * La salida para lo que no está en la lista, sin texto: "no está en la lista"
     * queda registrado como tal y el equipo lo ve para que Fundación amplíe el
     * catálogo. Es una opción de la definición y no una entrada del catálogo,
     * porque no es una droga.
     */
    fueraDeLista: opcionDeRespuestaSchema,
});
// ─────────────────────────────────────────────────────────────────────────────
// Grupo · filas de preguntas escalares
// ─────────────────────────────────────────────────────────────────────────────
/** Los campos que entran en una fila de un grupo: todos menos otro grupo. */
export const campoEscalarSchema = z.discriminatedUnion('tipo', [
    campoPuntajeSchema,
    campoOpcionSchema,
    campoBinarioSchema,
    campoNumeroSchema,
    campoFechaSchema,
    campoCatalogoSchema,
]);
/**
 * `id` de una pregunta de fila. Kebab-case y corto: el `linkId` en FHIR es
 * `<grupo>.<pregunta>`, y tiene que ser único en todo el `Questionnaire`.
 */
const idDePreguntaDeFila = z
    .string()
    .min(1)
    .max(20)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'el id de una pregunta de fila es kebab-case');
/**
 * Una pregunta dentro de la fila de un grupo.
 *
 * Sin `cuando` ni `opcional`: una fila se responde entera (una droga sin su
 * potencia no es un dato) y la condición vive en el grupo, no adentro. Sin
 * `presentacion`: dentro de una fila cada tipo tiene su control propio.
 */
export const preguntaDeFilaSchema = z.strictObject({
    id: idDePreguntaDeFila,
    texto: z.string().min(1),
    campo: campoEscalarSchema,
    ayuda: z.string().min(1).optional(),
});
export const campoGrupoSchema = z.strictObject({
    tipo: z.literal('grupo'),
    /** Cuántas filas como mínimo y como máximo. Un grupo vacío es `minimo: 0`. */
    minimo: z.number().int().min(0),
    maximo: z.number().int().min(1).max(20),
    /** Cómo se nombra una fila en la pantalla: "Medicamento 2". */
    etiquetaDeFila: z.string().min(1),
    preguntas: z.array(preguntaDeFilaSchema).min(1),
});
export const campoSchema = z.discriminatedUnion('tipo', [
    campoPuntajeSchema,
    campoOpcionSchema,
    campoBinarioSchema,
    campoNumeroSchema,
    campoFechaSchema,
    campoCatalogoSchema,
    campoGrupoSchema,
]);
// ─────────────────────────────────────────────────────────────────────────────
// Respuestas
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Lo que responde una persona a una pregunta escalar.
 *
 * `number` sin `.int()` desde E3.25: la potencia admite decimales. Que el
 * puntaje siga siendo entero lo decide su campo (`schemaDelCampo`), no la forma
 * general de la respuesta. La fecha y el código de catálogo son `string`.
 */
export const respuestaEscalarSchema = z.union([z.number(), z.string().max(40), z.boolean()]);
/** Una fila de un grupo: sus respuestas, por `id` de pregunta de fila. */
export const filaDeGrupoSchema = z.record(z.string().min(1).max(20), respuestaEscalarSchema);
/** Lo que responde una persona: un valor escalar, o las filas de un grupo. */
export const respuestaSchema = z.union([
    respuestaEscalarSchema,
    z.array(filaDeGrupoSchema).max(20),
]);
/** Fecha de calendario, `AAAA-MM-DD`. Es lo que guarda un campo `fecha`. */
export const fechaIsoSchema = z.iso.date();
// ─────────────────────────────────────────────────────────────────────────────
// Validación y puntaje de un campo
// ─────────────────────────────────────────────────────────────────────────────
/** Si el valor tiene a lo sumo `decimales` cifras después de la coma. */
function tieneDecimales(valor, decimales) {
    const escalado = valor * 10 ** decimales;
    // Tolerancia de coma flotante: 2.3 * 10 es 22.999999999999996.
    return Math.abs(escalado - Math.round(escalado)) < 1e-9;
}
function schemaDelNumero(campo) {
    return z
        .number()
        .min(campo.minimo)
        .max(campo.maximo)
        .refine((valor) => tieneDecimales(valor, campo.decimales), {
        message: `admite hasta ${campo.decimales} decimales`,
    });
}
function schemaDeLaFecha(campo, contexto) {
    // Las fechas ISO de calendario se comparan como texto: `AAAA-MM-DD` ordena
    // igual que el calendario, sin zona ni hora que la corran de día.
    return fechaIsoSchema
        .refine((valor) => !campo.noFutura || !contexto.hoy || valor <= contexto.hoy, {
        message: 'la fecha no puede ser futura',
    })
        .refine((valor) => !campo.desdeElDiaCero || !contexto.diaCero || valor >= contexto.diaCero, {
        message: 'la fecha no puede ser anterior al egreso',
    });
}
function schemaDelCatalogo(campo, contexto) {
    const conocidos = contexto.catalogos?.[campo.catalogo];
    return z
        .string()
        .refine((valor) => valor === campo.fueraDeLista.codigo ||
        (codigoDeCatalogoSchema.safeParse(valor).success &&
            (conocidos === undefined || conocidos.has(valor))), { message: `la respuesta tiene que ser una entrada del catálogo ${campo.catalogo}` });
}
function schemaDelGrupo(campo, contexto) {
    const fila = z.strictObject(Object.fromEntries(campo.preguntas.map((pregunta) => [pregunta.id, schemaDelCampo(pregunta.campo, contexto)])));
    return z.array(fila).min(campo.minimo).max(campo.maximo);
}
/**
 * El schema Zod que valida la respuesta a este campo.
 *
 * Se deriva del campo y no se escribe a mano en ningún lado: es lo que hace que
 * el rango declarado sea el rango que se valida, en la API y en la PWA, sin dos
 * listas que se puedan separar.
 */
export function schemaDelCampo(campo, contexto = {}) {
    switch (campo.tipo) {
        case 'puntaje':
            return z.number().int().min(campo.minimo).max(campo.maximo);
        case 'binario':
            return z.boolean();
        case 'numero':
            return schemaDelNumero(campo);
        case 'fecha':
            return schemaDeLaFecha(campo, contexto);
        case 'catalogo':
            return schemaDelCatalogo(campo, contexto);
        case 'grupo':
            return schemaDelGrupo(campo, contexto);
        case 'opcion': {
            const codigos = campo.opciones.map((opcion) => opcion.codigo);
            return z.string().refine((valor) => codigos.includes(valor), {
                message: `la respuesta tiene que ser una de: ${codigos.join(', ')}`,
            });
        }
    }
}
export function respuestaEsValida(campo, valor, contexto = {}) {
    return schemaDelCampo(campo, contexto).safeParse(valor).success;
}
/**
 * Cuántos puntos aporta una respuesta.
 *
 * `null` significa "esta pregunta no puntúa" y es distinto de cero: el dolor 0
 * es un dato con valor 0, y el P1 del microcheck no tiene puntaje ninguno.
 * Confundirlos haría que una batería sin escalas "sume 0" en vez de no sumar.
 *
 * Los cuatro tipos de E3.25 **nunca puntúan**: son datos del cuestionario de
 * hito, no ítems de un instrumento, y ninguno mueve el semáforo (R-08).
 */
export function puntosDeLaRespuesta(campo, valor) {
    switch (campo.tipo) {
        case 'puntaje':
            return campo.puntua ? valor : null;
        case 'binario':
            return valor === true ? campo.puntosSi : campo.puntosNo;
        case 'opcion':
            return campo.opciones.find((candidata) => candidata.codigo === valor)?.puntos ?? null;
        default:
            return null;
    }
}
/** Máximo alcanzable del campo. Sirve para verificar el rango del instrumento. */
export function puntosMaximosDelCampo(campo) {
    switch (campo.tipo) {
        case 'puntaje':
            return campo.puntua ? campo.maximo : 0;
        case 'binario':
            return Math.max(campo.puntosSi, campo.puntosNo);
        case 'opcion':
            return Math.max(...campo.opciones.map((opcion) => opcion.puntos ?? 0));
        default:
            return 0;
    }
}
/** Mínimo alcanzable del campo. */
export function puntosMinimosDelCampo(campo) {
    switch (campo.tipo) {
        case 'puntaje':
            return campo.puntua ? campo.minimo : 0;
        case 'binario':
            return Math.min(campo.puntosSi, campo.puntosNo);
        case 'opcion':
            return Math.min(...campo.opciones.map((opcion) => opcion.puntos ?? 0));
        default:
            return 0;
    }
}
/** Si el campo es de los que se responden con una sola respuesta escalar. */
export function esCampoEscalar(campo) {
    return campo.tipo !== 'grupo';
}
//# sourceMappingURL=tipos.js.map